import { Constants, _adapterRegistry } from '../shared/Constants';
import { DataTableFilters } from '../features/filters/DataTableFilters';
import {
    createToolbarButtonController,
    createToolbarItemController,
    isToolbarButtonVisible,
    normalizeBootstrapIcon
} from '../features/toolbar/toolbarButtonController';
import { buildColumnKeyMap, createColumnAccessor, resolveColumn, resolveRowDataFromElement } from './columnAccess';
import { getDataByScope, getDeprecationSuggestion, getSelectedData as collectSelectedData } from './AdapterData';

/**
 * DataTableAdapter Class (Runtime)
 * @template T
 *
 * Selection is implemented entirely in this class and does NOT require the
 * DataTables Select extension.  It tracks selected row indexes in a Set,
 * applies/removes a CSS `selected` class for visual feedback (matching the
 * existing DataTableWrapper.css rules), and exposes the same public API
 * whether or not the extension is present.
 */
export class DataTableAdapter {
    /**
     * @param {any} instance
     * @param {any} config
     * @param {any} actionCallbacks
     * @param {any} options
     */
    constructor(instance, config, actionCallbacks, options = {}) {
        this._instance = instance;
        this._config = config;
        this._actionCallbacks = actionCallbacks || {};
        this._selectionMode = options.selectionMode || Constants.Selection.NONE;
        this._hasCheckboxCol = options.hasCheckboxCol || false;
        this._selectionCallbacks = options.selectionCallbacks || {};
        // Internal selection state — keyed by DataTables row index
        this._selectedRows = new Set();
        this._useNativeSelect = !!($.fn.DataTable && $.fn.DataTable.select && config.select);
        
        // Advanced Filtering State
      this._advancedFilters = {}; // { colIdx: { type, val, min, max } }
        this._options = options;
        this._columnKeys = buildColumnKeyMap(config.columns);
        this._checkboxSortIndexes = new Set(
            (config.columns || [])
                .map((c, i) => ((c._isCheckbox && c._checkboxSortable) || c._sortKey ? i : -1))
                .filter((i) => i >= 0)
        );
      this._deferredAjax = options.deferredAjax || null;
      _adapterRegistry.set(this._instance.table().node(), this);

        this._initEvents(options);
    }
    get instance() {
      return this._instance;
    }

    /**
     * Map of column data keys for ergonomic lookups: adapter.getColumn(adapter.columns.Email)
     */
    get columns() {
        return this._columnKeys;
    }

    /**
     * Returns column metadata and helpers for a data key or column index.
     * @param {string|number} nameOrIndex
     * @returns {{ index: number, data: string|null, title: string, config: object, isVisible: boolean, getRow: Function, show: Function, hide: Function, toggle: Function } | null}
     */
    getColumn(nameOrIndex) {
        const accessor = createColumnAccessor(this, nameOrIndex);
        if (!accessor) {
            console.warn(`DataTableAdapter: Column "${nameOrIndex}" not found.`);
        }
        return accessor;
    }

    _resolveColumn(nameOrIndex) {
        return resolveColumn(this, nameOrIndex);
    }

    /**
     * @param {JQuery} $target
     * @returns {*|undefined}
     */
    _resolveRowDataFromTarget($target) {
        return resolveRowDataFromElement(this._instance, $target);
    }

    // ── Internal helpers ─────────────────────────────────────────────────

    _hasSelection() {
        return this._selectionMode !== Constants.Selection.NONE && (this._hasCheckboxCol || this._selectionMode !== Constants.Selection.NONE);
    }

    /** Check if a row is selectable based on ColumnBuilder._checkboxCondition. */
    _isRowSelectable(rowData) {
        if (!rowData) return true;
        const checkboxCol = this._config.columns?.find(c => c._isCheckbox);
        if (checkboxCol && typeof checkboxCol._checkboxCondition === 'function') {
            return Boolean(checkboxCol._checkboxCondition(rowData));
        }
        return true;
    }

    /** Whether the row at this DataTables index is currently selected. */
    isRowSelectedIndex(rowIdx) {
        if (this._useNativeSelect) return this._instance.row(rowIdx).isSelected ? this._instance.row(rowIdx).isSelected() : false;
        return this._selectedRows.has(rowIdx);
    }

    /**
     * DataTables keeps the first sort key it rendered. A later tick does not
     * change row data, so the cache still says unchecked and the row stays put.
     */
    _clearCheckboxSortCache() {
        if (!this._checkboxSortIndexes.size || !this._instance) return;
        const rows = this._instance.settings()[0]?.aoData;
        if (!rows) return;
        for (let i = 0; i < rows.length; i++) rows[i]._aSortData = null;
    }

    /**
     * Re-sort when a checkbox column made sortable via ColumnBuilder.sortable()
     * is the active sort, so ticking a row moves it into place.
     * Batched to one draw per tick batch (select-all loops).
     */
    _maybeResortByCheckbox() {
        if (this._resortScheduled || this._config.serverSide) return;
        const order = typeof this._instance.order === 'function' ? this._instance.order() : [];
        if (!order || !order.some(([index]) => this._checkboxSortIndexes.has(index))) return;
        this._resortScheduled = true;
        Promise.resolve().then(() => {
            this._resortScheduled = false;
            if (!this._instance) return;
            this._clearCheckboxSortCache();
            this._instance.draw(false);
        });
    }

    /** Toggle or force-set a row's selected state (manual mode). */
    _setRowSelected($tr, selected) {
        const rowIdx = this._instance.row($tr).index();
        if (rowIdx === undefined || rowIdx === null) return;

        const rowData = this._instance.row($tr).data();

        if (selected) {
            if (!this._isRowSelectable(rowData)) {
                return; // Disallow selecting rows failing checkbox condition
            }

            if (this._selectionMode === Constants.Selection.SINGLE) {
                // Deselect all others first
                this._selectedRows.forEach(idx => {
                    const $other = $(this._instance.row(idx).node());
                    $other.removeClass('selected');
                    $other.find('td.select-checkbox input.dt-row-select').prop('checked', false);
                    $other.find('td.select-checkbox input[type=checkbox]').not('.dt-row-select').prop('checked', false);
                });
                this._selectedRows.clear();
            }
            this._selectedRows.add(rowIdx);
            $tr.addClass('selected');

            // Fire onSelect callback if provided
            if (this._selectionCallbacks.onSelect) {
                const shouldSelect = this._selectionCallbacks.onSelect(rowData, $tr, this);
                if (shouldSelect === false) {
                    this._selectedRows.delete(rowIdx);
                    $tr.removeClass('selected');
                    return; // Reject selection
                }
            }
        } else {
            this._selectedRows.delete(rowIdx);
            $tr.removeClass('selected');

            // Fire onDeselect callback if provided
            if (this._selectionCallbacks.onDeselect) {
                this._selectionCallbacks.onDeselect(rowData, $tr, this);
            }
        }
        this._clearCheckboxSortCache();
        this._maybeResortByCheckbox();
    }

    /** Select all rows on the current page (manual mode). */
    _selectAllCurrentPage() {
        // Fire onSelectAll callback if provided
        if (this._selectionCallbacks.onSelectAll) {
            const shouldSelectAll = this._selectionCallbacks.onSelectAll(this);
            if (shouldSelectAll === false) return false; // Reject select all
        }

        const adapter = this;
        this._instance.rows({ page: 'current' }).indexes().each(function (rowIdx) {
            const node = adapter._instance.row(rowIdx).node();
            if (!node) return;

            const rowData = adapter._instance.row(rowIdx).data();
            if (!adapter._isRowSelectable(rowData)) return; // Skip unselectable rows

            const $tr = $(node);
            if (!$tr.hasClass('selected')) {
                adapter._setRowSelected($tr, true);
            }
        });

        return true;
    }

    /** Deselect all rows (manual mode). */
    _deselectAll() {
        // Fire onDeselectAll callback if provided
        if (this._selectionCallbacks.onDeselectAll) {
            this._selectionCallbacks.onDeselectAll(this);
        }

        Array.from(this._selectedRows).forEach(idx => {
            const node = this._instance.row(idx).node();
            if (node) this._setRowSelected($(node), false);
        });
    }

    /** Sync the header checkbox and all row checkbox inputs to current state. */
    _syncCheckboxVisuals($container) {
        const $headerCheck = $container.find('.dt-header-select-all');

        let selectableOnPageCount = 0;

        // Sync each row's checkbox input
        this._instance.rows({ page: 'current' }).indexes().each((rowIdx) => {
            const node = this._instance.row(rowIdx).node();
            if (!node) return;

            const $tr = $(node);
            const rowData = this._instance.row(rowIdx).data();
            const isSelectable = this._isRowSelectable(rowData);

            if (isSelectable) {
                selectableOnPageCount++;
                $tr.find('td.select-checkbox').removeClass('pe-none opacity-50');
            } else {
                $tr.find('td.select-checkbox').addClass('pe-none opacity-50');
            }

            const isSelected = $tr.hasClass('selected');
            $tr.find('td.select-checkbox input.dt-row-select').prop('checked', isSelected);
            // Also sync any custom-rendered checkboxes in the cell
            $tr.find('td.select-checkbox input[type=checkbox]').not('.dt-row-select').prop('checked', isSelected);
        });

        // Sync header checkbox
        if ($headerCheck.length === 0) return;
        const totalRows = this._instance.rows({ page: 'current' }).count();
        const selectableRows = selectableOnPageCount > 0 ? selectableOnPageCount : totalRows;

        const selectedOnPage = this._instance.rows({ page: 'current' }).indexes().toArray()
            .filter(i => this._selectedRows.has(i)).length;

        if (selectedOnPage === 0) {
            $headerCheck.prop('checked', false).prop('indeterminate', false).data('dt-indeterminate', false);
        } else if (selectedOnPage >= selectableRows) {
            $headerCheck.prop('checked', true).prop('indeterminate', false).data('dt-indeterminate', false);
        } else {
            $headerCheck.prop('checked', false).prop('indeterminate', true).data('dt-indeterminate', true);
        }
    }

    // ── Event wiring ─────────────────────────────────────────────────────

    _initEvents(options) {
        const self = this;
        const $table = $(this._instance.table().node());
        const $container = $(this._instance.table().container());

        // ── Ajax / response error handling ────────────────────────────────
        // DataTables emits `error.dt` for ajax failures, invalid JSON, and other processing issues.
        // We suppress the default alert (via ext.errMode='none') and surface a friendlier message.
        const errorEvent = 'error.dt.dtWrapperAjax';
        self._instance.off(errorEvent);
        self._instance.on(errorEvent, function (e, settings, techNote, message) {
            const tableId = $(settings?.nTable).attr('id') || '(unknown table)';
            const msg = message || 'Unknown DataTables error.';
            console.error(`[DataTableWrapper] DataTables error for table id=${tableId}.`, { techNote, message: msg });

            // Best-effort user-facing message (optional, only if GenericModal is present).
            if (window.Modal && typeof window.Modal.error === 'function') {
                const body = `${msg}\n\nTable: ${tableId}\nTech note: ${techNote ?? 'n/a'}`;
                window.Modal.error(body, { title: 'Table request failed' });
            }
        });

        // ── Action button delegation (namespaced; survives rebuild via registry lookup) ──
        const actionClickEvent = 'click.dtWrapperActions';
        $table.off(actionClickEvent);
        $table.on(actionClickEvent, '[data-action]', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const $btn = $(this);
            const actionKey = $btn.attr('data-action');
            const $tr = $btn.closest('tr');
            const tableNode = $table[0];
            const adapter = _adapterRegistry.get(tableNode);
            if (!adapter) return;

            const rowData = adapter._resolveRowDataFromTarget($btn);

            if (adapter._actionCallbacks[actionKey]) {
                adapter._actionCallbacks[actionKey](rowData, $tr);
            } else {
                console.warn(`DataTableAdapter: No callback registered for action "${actionKey}".`);
            }
        });

        // ── Checkbox / selection wiring ─────────────────────────────────
        // Only wire if this table actually has a checkbox or selection mode.
        if (self._hasSelection() || self._hasCheckboxCol) {
            // Ticking the select-all box must not also sort the column.
            // DataTables binds its sort handler directly on the <th>, so a
            // bubbling container handler runs too late — intercept in capture.
            if (self._headerTickGuard) {
                $container[0].removeEventListener('click', self._headerTickGuard, true);
            }
            self._headerTickGuard = (e) => {
                if (e.target && e.target.classList && e.target.classList.contains('dt-header-select-all')) {
                    e.stopPropagation();
                    return;
                }
                const th = e.target && e.target.closest && e.target.closest('th');
                if (th && th.classList.contains('dt-checkbox-sortable')) self._clearCheckboxSortCache();
            };
            $container[0].addEventListener('click', self._headerTickGuard, true);

            if (self._useNativeSelect) {
            // ── Native Select extension path ────────────────────────────
            // Header checkbox delegates from container (survives thead redraws)
            $container.on('change', '.dt-header-select-all', function () {
                if ($(this).is(':checked')) {
                    // Fire onSelectAll callback if provided
                    if (self._selectionCallbacks.onSelectAll) {
                        const shouldSelectAll = self._selectionCallbacks.onSelectAll(self);
                        if (shouldSelectAll === false) {
                            $(this).prop('checked', false);
                            return;
                        }
                    }
                    self._instance.rows({ page: 'current' }).select();
                } else {
                    // Fire onDeselectAll callback if provided
                    if (self._selectionCallbacks.onDeselectAll) {
                        self._selectionCallbacks.onDeselectAll(self);
                    }
                    self._instance.rows({ page: 'current' }).deselect();
                }
            });

            // Intercept user-select to prevent DataTables Select extension from selecting unselectable rows on click
            self._instance.on('user-select', function (e, dt, type, cell, originalEvent) {
                const rowIdx = cell.index().row;
                const rowData = self._instance.row(rowIdx).data();
                if (!self._isRowSelectable(rowData)) {
                    e.preventDefault();
                }
            });

            self._instance.on('select', function (e, dt, type, indexes) {
                indexes.each(idx => {
                    const $tr = $(self._instance.row(idx).node());
                    const rowData = self._instance.row(idx).data();

                    // Immediately deselect if row fails checkbox condition
                    if (!self._isRowSelectable(rowData)) {
                        self._instance.row(idx).deselect();
                        return;
                    }

                    // Fire onSelect for each selected row
                    if (self._selectionCallbacks.onSelect) {
                        const shouldSelect = self._selectionCallbacks.onSelect(rowData, $tr, self);
                        if (shouldSelect === false) {
                            self._instance.row(idx).deselect();
                        }
                    }
                });
                self._syncCheckboxVisuals($container);
                self._clearCheckboxSortCache();
                self._maybeResortByCheckbox();
            });

            self._instance.on('deselect', function (e, dt, type, indexes) {
                // Fire onDeselect for each deselected row
                if (self._selectionCallbacks.onDeselect) {
                    indexes.each(idx => {
                        const $tr = $(self._instance.row(idx).node());
                        const rowData = self._instance.row(idx).data();
                        self._selectionCallbacks.onDeselect(rowData, $tr, self);
                    });
                }
                self._syncCheckboxVisuals($container);
                self._clearCheckboxSortCache();
                self._maybeResortByCheckbox();
            });

            self._instance.on('draw', function () {
                self._syncCheckboxVisuals($container);
            });

        } else {
            // ── Manual selection path (Select extension absent) ─────────

            // Row checkbox cell click
            $table.on('click', 'td.select-checkbox', function (e) {
                e.stopPropagation();
                const $tr = $(this).closest('tr');
                const rowData = self._instance.row($tr).data();
                if (!self._isRowSelectable(rowData)) return;

                const isSelected = $tr.hasClass('selected');
                self._setRowSelected($tr, !isSelected);
                self._syncCheckboxVisuals($container);
            });

            // Header checkbox — delegated from container
            $container.on('change', '.dt-header-select-all', function (e) {
                e.stopPropagation();
                const $checkbox = $(this);
                const wasIndeterminate = $checkbox.data('dt-indeterminate') === true;
                const isChecked = $checkbox.prop('checked');
                
                // From a partial selection, clicking the header clears the current selection.
                if (wasIndeterminate || !isChecked) {
                    self._deselectAll();
                } else {
                    const selected = self._selectAllCurrentPage();
                    if (selected === false) {
                        $checkbox.prop('checked', false).prop('indeterminate', false);
                        return;
                    }
                }
                self._syncCheckboxVisuals($container);
            });

            // Re-sync visuals after every draw (pagination, search, etc.)
            self._instance.on('draw', function () {
                // Re-apply .selected class to rows that are in the selected set
                self._instance.rows({ page: 'current' }).indexes().each(function (rowIdx) {
                    const node = self._instance.row(rowIdx).node();
                    if (!node) return;

                    const $tr = $(node);
                    if (self._selectedRows.has(rowIdx)) {
                        $tr.addClass('selected');
                    } else {
                        $tr.removeClass('selected');
                    }
                });
                self._syncCheckboxVisuals($container);
            });
        }
    }

    // ── Jump to Page wiring ─────────────────────────────────────────
    this._instance.on('draw', () => {
        this._renderSummary();
    });

    if (options.jumpToPage) {
        this._initJumpToPage($container);
    }
}

    /**
     * Initializes the "Jump to Page" input logic.
     * @param {jQuery} $container - The DataTables wrapper container
     */
    _initJumpToPage($container) {
        const self = this;
        let $placeholder = $container.find('.dt-jump-to-page');
        
        // Fallback: If DataTables dom parsing didn't create the div, try to find it or inject it
        if ($placeholder.length === 0) {
            const $paging = $container.find('.dt-pagination, .dataTables_paginate');
            if ($paging.length) {
                $placeholder = $('<div class="dt-jump-to-page"></div>').insertBefore($paging);
            } else {
                return; // No pagination area found
            }
        }

        const navigate = (val) => {
            let page = parseInt(val);
            if (isNaN(page)) return;
            
            const info = self._instance.page.info();
            if (page < 1) page = 1;
            if (page > info.pages) page = info.pages;

            self._instance.page(page - 1).draw('page');
        };

        // Sync helper - handles both data sync and DOM persistence
        const syncState = () => {
            const info = self._instance.page.info();

            // RE-INJECT HTML if it was cleared by a footer re-render (common in Bootstrap 5)
            if ($placeholder.children().length === 0) {
                $placeholder.html(`
                    <div class="dt-jump-wrapper d-flex align-items-center">
                        <span class="dt-jump-label">Go to</span>
                        <input type="number" class="form-control form-control-sm dt-jump-input" min="1" step="1" value="${info.page + 1}">
                    </div>
                `);

                const $newInput = $placeholder.find('.dt-jump-input');
                $newInput.on('change', function() { navigate($(this).val()); });
                $newInput.on('keyup', function(e) {
                    if (e.key === 'Enter') navigate($(this).val());
                });
            }

            const $input = $placeholder.find('.dt-jump-input');
            $input.attr('max', info.pages);
            $input.val(info.page + 1);
            
            // Hide jump-to-page if there's only one page
            if (info.pages <= 1) {
                $placeholder.addClass('d-none');
            } else {
                $placeholder.removeClass('d-none');
            }
        };

        // Sync input state on every draw
        this._instance.on('draw', syncState);
        
        // Initial sync in case draw already happened
        syncState();
    }

    _initColumnFilters() {
        return DataTableFilters.init(this);
    }

    _renderFilterBody($body, colIdx, col) {
        return DataTableFilters.renderBody(this, $body, colIdx, col);
    }

    /**
     * Applies the filter based on the UI state in the popover body.
     */
    _applyFilter($body, colIdx, col, $toggle) {
        const type = col.filterType;
        const column = this._instance.column(colIdx);
        let hasValue = false;

        if (type === Constants.FilterType.RANGE || type === Constants.FilterType.DATE_RANGE) {
            const $min = $body.find('.dt-filter-min');
            const $max = $body.find('.dt-filter-max');

            // If GenericDatepicker is used, get the Date object directly
            const gdpMin = $min.data('generic-datepicker');
            const gdpMax = $max.data('generic-datepicker');

            const min = gdpMin ? gdpMin.val() : $min.val();
            const max = gdpMax ? gdpMax.val() : $max.val();
            
            if (min || max) {
                this._advancedFilters[colIdx] = { type, min, max };
                hasValue = true;
            } else {
                delete this._advancedFilters[colIdx];
            }
            // Ranges require a global redraw to trigger the custom search extension
            this._instance.draw();
        } else if (type === Constants.FilterType.SELECT) {
            const val = $body.find('.dt-filter-select').val();
            column.search(val).draw();
            hasValue = !!val;
        } else if (type === 'custom' && col.filterConfig && typeof col.filterConfig.onApply === 'function') {
            const val = $body.find('.form-control').val();
            col.filterConfig.onApply(val, column, $body);
            hasValue = !!val;
        } else {
            const val = $body.find('.form-control').val();
            column.search(val).draw();
            hasValue = !!val;
        }

        if (hasValue) $toggle.addClass('active');
        else $toggle.removeClass('active');
    }

    /**
     * Clears the filter for a specific column.
     */
    _clearFilter(colIdx, col, $toggle) {
        // Cleanup: Destroy GenericDatepicker instances if they exist
        this._cleanupPickers($('.dt-filter-popover-body'));

        delete this._advancedFilters[colIdx];
        this._instance.column(colIdx).search('').draw();
        $toggle.removeClass('active');
    }

    /**
     * Internal helper to clean up GenericDatepicker instances within a container.
     */
    _cleanupPickers($container) {
        $container.find('.dt-filter-min, .dt-filter-max, .gdp-input').each(function() {
            const gdp = $(this).data('generic-datepicker');
            if (gdp && typeof gdp.destroy === 'function') {
                gdp.destroy();
            }
        });
    }

    /**
     * The core logic for $.fn.dataTable.ext.search.
     * Decides if a row should be visible based on advanced filters.
     */
    _applyAdvancedFilters(data, dataIndex) {
        const activeKeys = Object.keys(this._advancedFilters);
        if (activeKeys.length === 0) return true;

        for (const colIdx of activeKeys) {
            const filter = this._advancedFilters[colIdx];
            const rawVal = data[colIdx];
            const col = this._config.columns[colIdx];
            
            if (filter.type === Constants.FilterType.RANGE) {
                const val = parseFloat(rawVal);
                const min = filter.min ? parseFloat(filter.min) : -Infinity;
                const max = filter.max ? parseFloat(filter.max) : Infinity;
                if (isNaN(val) || val < min || val > max) return false;
            }
            
            if (filter.type === Constants.FilterType.DATE_RANGE) {
                const gd = window.GenericDateTime ? window.GenericDateTime.from(rawVal) : null;
                if (!gd || !gd.isValid()) return true;
                
                if (filter.min && gd.isBefore(filter.min, 'day')) return false;
                if (filter.max && gd.isAfter(filter.max, 'day')) return false;
            }
        }

        return true;
    }

    // ── Public API ───────────────────────────────────────────────────────

    reload(callback, resetPaging = false) {
        // If initial load was deferred, restore ajax config first.
        if (this._deferredAjax) {
            return this.loadData(callback, resetPaging);
        }
        if (this._instance.ajax && typeof this._instance.ajax.reload === 'function') {
            this._instance.ajax.reload(callback, resetPaging);
        }
        return this;
    }

    /**
     * Triggers the initial AJAX load that was deferred via withDeferLoad().
     * Only has an effect on the first call; subsequent calls delegate to reload().
     * Handles all ajax shapes: string URL, object { url, type, data }, or function.
     * @param {Function} [callback] - Optional callback after data is loaded.
     * @param {boolean} [resetPaging=false]
     * @returns {this}
     */
    loadData(callback, resetPaging = false) {
        if (this._deferredAjax) {
            const settings = this._instance.settings()[0];
            settings.ajax = this._deferredAjax;
            this._deferredAjax = null;
            this._instance.ajax.reload(callback, resetPaging);
        } else {
            this.reload(callback, resetPaging);
        }
        return this;
    }

    draw(paging = false) {
        this._instance.draw(paging);
        return this;
    }

    /**
     * Semantic alias for draw(). Updates the table UI to reflect data changes.
     * @param {boolean} paging
     * @returns {this}
     */
    refresh(paging = false) {
        return this.draw(paging);
    }

    /**
     * Returns row data for a given table view.
     * @deprecated Use dedicated methods: getAllData(), getFilteredData(), getPageData(), getSelectedData()
     * @param {'all'|'filtered'|'visible'|'page'|'current-page'|'selected'} [scope='filtered']
     * @returns {T[]}
     */
    getData(scope = 'filtered') {
        const validScopes = Object.values(Constants.Scope);
        if (!validScopes.includes(scope)) {
            console.warn(`DataTableAdapter: Invalid scope "${scope}". Valid scopes: ${validScopes.join(', ')}. Use dedicated methods like getAllData(), getFilteredData(), etc.`);
        }
        if (scope !== 'filtered' && scope !== 'visible') {
            console.warn(`DataTableAdapter: getData('${scope}') is deprecated. Use ${this._getDeprecationSuggestion(scope)} instead.`);
        }
        return getDataByScope(this, scope);
    }

    /**
     * Returns all rows in the table (ignores paging, search, and filters).
     * @returns {T[]}
     */
    getAllData() {
        return this._instance.rows().data().toArray();
    }

    /**
     * Returns rows matching the current search/filter criteria.
     * @returns {T[]}
     */
    getFilteredData() {
        return this._instance.rows({ search: 'applied' }).data().toArray();
    }

    /**
     * Alias for getFilteredData(). Returns rows matching current search/filter.
     * @returns {T[]}
     */
    getVisibleData() {
        return this.getFilteredData();
    }

    /**
     * Returns rows on the current page (after paging and search are applied).
     * @returns {T[]}
     */
    getPageData() {
        return this._instance.rows({ page: 'current', search: 'applied' }).data().toArray();
    }

    /**
     * Alias for getPageData(). Returns rows on the current page.
     * @returns {T[]}
     */
    getCurrentPageData() {
        return this.getPageData();
    }

    /**
     * Returns the currently selected row(s).
     * @returns {T[]}
     */
    getSelectedData() {
        return collectSelectedData(this);
    }

    /**
     * Internal helper to get deprecation suggestion message.
     * @private
     */
    _getDeprecationSuggestion(scope) {
        return getDeprecationSuggestion(scope);
    }

    /**
     * Forces DataTables to recalculate column widths.
     * Crucial for tables inside modals or hidden tabs.
     */
    recalculate() {
        if (this._instance && typeof this._instance.columns.adjust === 'function') {
            this._instance.columns.adjust();
        }
        return this;
    }

    /**
     * Changes the number of rows displayed per page.
     * If the new length is not in lengthMenu, it will be added and sorted.
     * @param {number} length - The new page length (rows per page)
     * @returns {this}
     */
    setPageLength(length) {
        if (typeof length !== 'number' || length < 1) {
            console.warn(`DataTableAdapter: Invalid page length "${length}". Must be a positive number.`);
            return this;
        }

        // Update lengthMenu if necessary
        if (this._config.lengthMenu) {
            let values = this._config.lengthMenu;
            
            // Handle 2D lengthMenu format [[values], [labels]]
            if (Array.isArray(this._config.lengthMenu[0])) {
                values = this._config.lengthMenu[0];
            }
            
            // Add new length if not in menu
            if (!values.includes(length)) {
                values.push(length);
                values.sort((a, b) => a - b);
                
                // If 2D format, keep labels in sync (usually numeric too)
                if (Array.isArray(this._config.lengthMenu[0])) {
                    this._config.lengthMenu[1] = this._config.lengthMenu[0].slice();
                }
            }
        }

        // Apply the new page length and redraw
        this._instance.page.len(length).draw();
        return this;
    }


    /**
     * Clears all row selections.
     * @returns {this}
     */
    clearSelection() {
      if (this._useNativeSelect) {
        this._instance.rows().deselect();
      } else {
        this._deselectAll();
      }
      this._syncCheckboxVisuals($(this._instance.table().container()));
      return this;
    }
    /**
     * Adds a single row to the table.
     * @param {T} data
     * @returns {this}
     */
    addRow(data) {
        this._instance.row.add(data);
        return this;
    }

    /**
     * Adds multiple rows to the table.
     * @param {T[]} dataArray
     * @returns {this}
     */
    addRows(dataArray) {
        this._instance.rows.add(dataArray);
        return this;
    }

    /**
     * Clears all data from the table.
     * @returns {this}
     */
    clear() {
        this._selectedRows.clear();
        this._instance.clear();
        return this;
    }

    _initSummary() {
        const summary = this._config.summary;
        if (!summary || !Array.isArray(summary.rows) || summary.rows.length === 0) {
            return;
        }

        const $container = $(this._instance.table().container());
        let $summary = $container.children('.dt-summary');

        if ($summary.length === 0) {
            $summary = $('<div class="dt-summary"></div>');
            const $footer = $container.children('.dt-core-footer').first();

            if ($footer.length) {
                $summary.insertBefore($footer);
            } else {
                $container.append($summary);
            }
        }

        this._summaryContainer = $summary;
        this._renderSummary();
    }

    _resolveSummaryValue(value, rows) {
        if (typeof value === 'function') {
            return value(rows, this);
        }

        return value;
    }

    _formatSummaryValue(cell, value, rows) {
        if (typeof cell?.format === 'function') {
            return cell.format(value, rows, this);
        }

        if (value === null || value === undefined || value === '') {
            return cell?.placeholder || '';
        }

        return value;
    }

    _renderSummary() {
        const summary = this._config.summary;
        if (!summary || !this._summaryContainer || this._summaryContainer.length === 0) {
            return;
        }

        const gap = typeof summary.gap === 'number' ? `${summary.gap}px` : summary.gap;
        const isNoGap = gap === 0 || gap === '0' || gap === '0px';

        const $container = $(this._instance.table().container());
        $container.toggleClass('has-dt-summary-no-gap', !!isNoGap);

        const rows = getDataByScope(this, summary.scope);

        if (summary.emptyText && rows.length === 0) {
            this._summaryContainer
                .attr('class', 'dt-summary')
                .addClass(summary.className || '')
                .addClass(`dt-summary-${summary.variant || 'default'}`)
                .toggleClass('dt-summary-no-gap', !!isNoGap)
                .css('margin-top', isNoGap ? '0' : (gap ?? ''))
                .html(`<div class="dt-summary-empty">${summary.emptyText}</div>`);
            return;
        }

        const rowHtml = summary.rows.map(row => {
            const cells = row.cells.slice(0, summary.valueColumns);

            while (cells.length < summary.valueColumns) {
                cells.push({ value: '', className: 'is-empty' });
            }

            const cellsHtml = cells.map(cell => {
                const rawValue = this._resolveSummaryValue(cell.value, rows);
                const content = this._formatSummaryValue(cell, rawValue, rows);
                const className = ['dt-summary-cell', cell.className || ''].filter(Boolean).join(' ');
                const idAttr = cell.id ? ` id="${cell.id}"` : '';

                return `<div class="${className}"${idAttr}>${content ?? ''}</div>`;
            }).join('');

            const rowClassName = ['dt-summary-row', row.className || ''].filter(Boolean).join(' ');
            const labelClassName = ['dt-summary-label', row.labelClassName || ''].filter(Boolean).join(' ');

            return `
                <div class="${rowClassName}" style="--dt-summary-value-columns:${summary.valueColumns};">
                    <div class="${labelClassName}">${row.label || ''}</div>
                    ${cellsHtml}
                </div>
            `;
        }).join('');

        this._summaryContainer
            .attr('class', 'dt-summary')
            .addClass(summary.className || '')
            .addClass(`dt-summary-${summary.variant || 'default'}`)
            .toggleClass('dt-summary-no-gap', !!isNoGap)
            .css('margin-top', isNoGap ? '0' : (gap ?? ''))
            .html(rowHtml);
    }

    /**
     * Resolves a theme name or hex color to a valid CSS color.
     * Integrates with GenericModal types if available.
     */
    _resolveColor(color) {
        return Constants.resolveColor(color);
    }

    getRowData(element) {
        return this._resolveRowDataFromTarget($(element));
    }

    /**
     * Sets the visibility of a column.
     * @param {string|number} nameOrIndex - Data property name or column index
     * @param {boolean} visible
     * @returns {this}
     */
    setColumnVisibility(nameOrIndex, visible) {
        const resolved = this._resolveColumn(nameOrIndex);
        if (resolved) {
            resolved.colApi.visible(visible);
        } else {
            console.warn(`DataTableAdapter: Column "${nameOrIndex}" not found.`);
        }
        return this;
    }

    /**
     * Shows a column.
     * @param {string|number} nameOrIndex
     * @returns {this}
     */
    showColumn(nameOrIndex) {
        return this.setColumnVisibility(nameOrIndex, true);
    }

    /**
     * Hides a column.
     * @param {string|number} nameOrIndex
     * @returns {this}
     */
    hideColumn(nameOrIndex) {
        return this.setColumnVisibility(nameOrIndex, false);
    }

    /**
     * Toggles the visibility of a column.
     * @param {string|number} nameOrIndex
     * @returns {this}
     */
    toggleColumn(nameOrIndex) {
        const resolved = this._resolveColumn(nameOrIndex);
        if (resolved) {
            resolved.colApi.visible(!resolved.colApi.visible());
        }
        return this;
    }

    /**
     * Initializes the core toolbar actions.
     * Renders buttons into the .dt-toolbar-actions container using GenericModal's pattern.
     */
    _initToolbar() {
        const self = this;
        const $container = $(this._instance.table().container());
        const $toolbar = $container.find('.dt-toolbar-actions');
        if ($toolbar.length === 0) return;

        const toolbarNs = '.dtWrapperToolbar';
        this._$toolbar = $toolbar;
        this._toolbarEventNs = toolbarNs;

        const buttons = this._config.toolbarButtons || [];

        // Render toolbar buttons (supports per-button icon-only via missing title)
        const htmlContent = buttons
            .filter(btn => isToolbarButtonVisible(btn, self))
            .map(btn => {
            // Segmented group buttons (single outer pill + dividers)
            if (btn.isGroup && Array.isArray(btn.groupItems) && btn.groupItems.length > 0) {
                const visibleItems = btn.groupItems.filter(item => isToolbarButtonVisible(item, self));
                if (visibleItems.length === 0) return '';

                const itemsHtml = visibleItems.map((item) => {
                    const hasText = item.title !== undefined && item.title !== null && item.title !== '';
                    const title = hasText ? item.title : '';
                    const tooltip = item.title ?? item.key ?? '';
                    const titleAttr = tooltip ? ` title="${tooltip}"` : '';
                    const aria = tooltip ? ` aria-label="${tooltip}"` : '';
                    const iconClass = normalizeBootstrapIcon(item.icon);
                    const iconOnly = !hasText && item.icon;
                    const iconHtml = iconClass ? `<i class="${iconClass}${iconOnly ? '' : ' me-2'}"></i>` : '';
                    const extraClass = (item.className || '').trim();
                    const bg = item.color ? `background-color:${Constants.resolveColor(item.color)};` : '';
                    const fg = item.textColor ? `color:${Constants.resolveColor(item.textColor)};` : (item.color ? 'color:#fff;' : '');
                    const colorStyle = (bg || fg) ? `${bg}${fg}border-left:none;` : 'background:#fff;color:#1f2937 !important;border:1px solid #e5e7eb;';
                    const labelHtml = hasText ? `<span class="gm-toolbar-item-label gm-group-btn-label">${title}</span>` : '';
                    const idAttr = item.id ? ` id="${String(item.id)}"` : ` id="${item.key}"`;
                    const gmBtnClass = ` gm-btn-${item.key}`;
                    return `<button type="button" class="btn gm-action-btn dt-toolbar-btn ${extraClass}${gmBtnClass}"${idAttr} data-group-parent="${btn.key}" data-group-item="${item.key}"${titleAttr}${aria} style="padding:5px 10px;font-size:14px;line-height:1;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;transition:all 0.2s ease;${colorStyle}">${iconHtml}${labelHtml}</button>`;
                }).join('');

                return `<div class="btn-group dt-group-container" role="group">${itemsHtml}</div>`;
            }

            const title = btn.title ?? '';
            const tooltip = title || btn.key || '';
            const iconClass = normalizeBootstrapIcon(btn.icon);
            const iconHtml = iconClass ? `<i class="${iconClass}${title ? ' me-1' : ''}"></i>` : '';
            const extraClass = (btn.className || '') + ` gm-btn-${btn.key} dt-toolbar-btn`;
            const fg = btn.textColor ? Constants.resolveColor(btn.textColor) : '#fff';
            const style = `background-color:${Constants.resolveColor(btn.color)};color:${fg};border:none;padding:5px 10px;font-size:14px;border-radius:6px;line-height:1;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all 0.2s ease;white-space:nowrap;font-weight:600;box-shadow:0 2px 4px rgba(0,0,0,0.1);`;
            const labelHtml = title ? `<span>${title}</span>` : '';
            const aria = tooltip ? ` aria-label="${tooltip}"` : '';
            const titleAttr = tooltip ? ` title="${tooltip}"` : '';

            const mainBtnHtml = `<button type="button" class="btn gm-action-btn ${extraClass}" data-action="${btn.key}"${titleAttr}${aria} style="${style}" onmouseover="this.style.filter='brightness(1.1)';this.style.transform='translateY(-1px)';this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)';" onmouseout="this.style.filter='none';this.style.transform='translateY(0)';this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)';">${iconHtml}${labelHtml}</button>`;

            const visibleSplitItems = (btn.isSplit && Array.isArray(btn.splitItems))
                ? btn.splitItems.filter(item => isToolbarButtonVisible(item, self))
                : [];

            if (visibleSplitItems.length === 0) {
                return mainBtnHtml;
            }

            // Split buttons should visually merge with no gap between primary and toggle.
            // IMPORTANT: Do not use overflow:hidden here; it clips the dropdown menu.
            // Also avoid per-button "lift" transforms which create a visible seam.
            const groupStyle = `display:inline-flex;gap:0;align-items:stretch;border-radius:6px;box-shadow:0 2px 4px rgba(0,0,0,0.1);position:relative;z-index:1;height:32px;`;
            const splitMainStyle = `${style}box-shadow:none;margin:0;height:100%;border-top-right-radius:0;border-bottom-right-radius:0;border-right:0;`;
            const caretStyle = `background-color:${Constants.resolveColor(btn.color)};color:${fg};border:none;padding:5px 10px;font-size:14px;border-radius:0;border-top-right-radius:6px;border-bottom-right-radius:6px;line-height:1;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all 0.2s ease;white-space:nowrap;font-weight:600;box-shadow:none;margin:0;height:100%;margin-left:-1px;`;
            const splitMainBtnHtml = `<button type="button" class="btn gm-action-btn ${extraClass}" data-action="${btn.key}"${titleAttr}${aria} style="${splitMainStyle}" onmouseover="this.style.filter='brightness(1.1)';" onmouseout="this.style.filter='none';">${iconHtml}${labelHtml}</button>`;
            const dividerClass = btn.splitDivider ? ' gm-split-divider' : '';
            const toggleHtml = `<button type="button" class="btn gm-action-btn dropdown-toggle dropdown-toggle-split gm-btn-${btn.key}-split-toggle dt-toolbar-btn${dividerClass}" data-bs-toggle="dropdown" aria-expanded="false" style="${caretStyle}" title="More" aria-label="More" onmouseover="this.style.filter='brightness(1.1)';" onmouseout="this.style.filter='none';"><span class="visually-hidden">Toggle Dropdown</span></button>`;

            const menuItemsHtml = visibleSplitItems.map(item => {
                const itemTitle = item.title ?? item.key ?? '';
                const itemIconClass = normalizeBootstrapIcon(item.icon);
                const itemIconHtml = itemIconClass ? `<i class="${itemIconClass} me-2"></i>` : '';
                const itemClass = (item.className || '').trim();
                const itemTooltip = itemTitle || item.key || '';
                const itemTitleAttr = itemTooltip ? ` title="${itemTooltip}"` : '';
                const itemAria = itemTooltip ? ` aria-label="${itemTooltip}"` : '';
                const itemLabelHtml = itemTitle
                    ? `<span class="gm-toolbar-item-label">${itemTitle}</span>`
                    : '';

                return `<a class="dropdown-item ${itemClass}" href="#" data-split-parent="${btn.key}" data-split-item="${item.key}"${itemTitleAttr}${itemAria}>${itemIconHtml}${itemLabelHtml}</a>`;
            }).join('');

            // Ensure the menu is not hidden behind DT header chrome.
            return `<div class="btn-group" role="group" style="${groupStyle}">${splitMainBtnHtml}${toggleHtml}<div class="dropdown-menu" style="z-index:9999;">${menuItemsHtml}</div></div>`;
        }).filter(Boolean).join('');

        const wrapper = `<div style="display:inline-flex;gap:8px;align-items:center;flex-wrap:nowrap;">${htmlContent}</div>`;

        $toolbar.html(wrapper);

        // Wire up toolbar events (namespaced; off-before-on so re-init does not stack handlers)
        $toolbar.off(toolbarNs);

        $toolbar.on(`click${toolbarNs}`, '[data-group-parent][data-group-item]', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const $item = $(this);
            const parentKey = $item.data('group-parent');
            const itemKey = $item.data('group-item');

            const groupConfig = buttons.find(b => b.key === parentKey);
            const groupItem = groupConfig?.groupItems?.find(i => i.key === itemKey);

            if (!groupConfig || !groupItem) {
                console.warn(`DataTableAdapter: Group item "${String(parentKey)}:${String(itemKey)}" not found.`);
                return;
            }

            const callback = typeof groupItem.callback === 'function'
                ? groupItem.callback
                : (typeof groupConfig.callback === 'function' ? groupConfig.callback : null);

            if (callback) {
                const groupBtn = createToolbarItemController(groupItem, $item);
                callback(self, groupBtn, groupItem);
                return;
            }

            console.warn(`DataTableAdapter: No callback for group item "${String(parentKey)}:${String(itemKey)}".`);
        });

        $toolbar.on(`click${toolbarNs}`, '[data-split-parent][data-split-item]', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const $item = $(this);
            const parentKey = $item.data('split-parent');
            const itemKey = $item.data('split-item');

            const btnConfig = buttons.find(b => b.key === parentKey);
            const splitItem = btnConfig?.splitItems?.find(i => i.key === itemKey);

            if (!btnConfig || !splitItem) {
                console.warn(`DataTableAdapter: Split item "${String(parentKey)}:${String(itemKey)}" not found.`);
                return;
            }

            if (typeof splitItem.callback === 'function') {
                const toolbarBtn = createToolbarItemController(splitItem, $item);
                splitItem.callback(self, toolbarBtn, splitItem);
                return;
            }

            console.warn(`DataTableAdapter: No callback for split item "${String(parentKey)}:${String(itemKey)}".`);
        });

        $toolbar.on(`click${toolbarNs}`, '[data-action]', function (e) {
            e.preventDefault();
            const $btn = $(this);
            const actionKey = $btn.data('action');
            const btnConfig = buttons.find(b => b.key === actionKey);

            if (btnConfig && typeof btnConfig.callback === 'function') {
                const toolbarBtn = createToolbarButtonController(btnConfig, $btn);
                btnConfig.callback(self, toolbarBtn);
            } else {
                // Default logic for standard export buttons
                switch (actionKey) {
                    case Constants.Buttons.CSV:
                        self._exportCSV();
                        break;
                    case Constants.Buttons.EXCEL:
                        self._exportExcel();
                        break;
                    case Constants.Buttons.PRINT:
                        self._print();
                        break;
                    case Constants.Buttons.COPY:
                        self._copyToClipboard();
                        break;
                    case Constants.Buttons.COLVIS:
                        self._showColVis();
                        break;
                    default:
                        console.warn(`DataTableAdapter: No default logic for toolbar action "${actionKey}".`);
                }
            }
        });
    }

    _exportCSV() {
        const data = this._instance.rows().data().toArray();
        if (data.length === 0) return;

        const columns = this._config.columns.filter(c => c.data !== null && !c._isCheckbox);
        const headers = columns.map(c => c.title);
        
        let csv = headers.join(',') + '\n';
        data.forEach(row => {
            const values = columns.map(col => {
                let val = row[col.data];
                if (val === null || val === undefined) val = '';
                // Escape quotes and wrap in quotes if contains comma
                val = String(val).replace(/"/g, '""');
                return val.includes(',') ? `"${val}"` : val;
            });
            csv += values.join(',') + '\n';
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `export_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    _exportExcel() {
        // Simplified Excel export via CSV (standard trick for lightweight libraries)
        this._exportCSV();
    }

    _print() {
        const data = this._instance.rows().data().toArray();
        if (data.length === 0) return;

        const columns = this._config.columns.filter(c => c.data !== null && !c._isCheckbox);
        const headers = columns.map(c => c.title);

        let html = `
            <html>
            <head>
                <title>Table Export</title>
                <style>
                    body { font-family: sans-serif; padding: 20px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    .header { text-align: center; margin-bottom: 30px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h2>Data Export</h2>
                    <p>Generated on ${new Date().toLocaleString()}</p>
                </div>
                <table>
                    <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
                    <tbody>
                        ${data.map(row => `<tr>${columns.map(col => `<td>${row[col.data] || ''}</td>`).join('')}</tr>`).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        const printWin = window.open('', '_blank');
        printWin.document.write(html);
        printWin.document.close();
        
        // Wait for content to load then print
        setTimeout(() => {
            printWin.print();
            printWin.close();
        }, 500);
    }

    _copyToClipboard() {
        const data = this._instance.rows().data().toArray();
        if (data.length === 0) return;

        const columns = this._config.columns.filter(c => c.data !== null && !c._isCheckbox);
        const headers = columns.map(c => c.title);
        
        let text = headers.join('\t') + '\n';
        data.forEach(row => {
            text += columns.map(col => row[col.data] || '').join('\t') + '\n';
        });

        const $temp = $('<textarea>');
        $('body').append($temp);
        $temp.val(text).select();
        document.execCommand('copy');
        $temp.remove();

        if (window.log) window.log('Copied to clipboard!', 'success');
    }

    destroy() {
        const tableNode = this._instance.table().node();
        const $table = $(tableNode);
        const tableId = $table.attr('id');
        if (tableId) {
            $(window).off('resize.dt-refresh-' + tableId);
        }

        // Clean up global search triggers if any were registered
        if (this._triggerNamespace) {
            $(document).off(this._triggerNamespace);
        }

        $table.off('click.dtWrapperActions');

        if (this._$toolbar?.length && this._toolbarEventNs) {
            this._$toolbar.off(this._toolbarEventNs);
        } else {
            const $container = $(this._instance.table().container());
            $container.find('.dt-toolbar-actions').off('.dtWrapperToolbar');
        }
        this._$toolbar = null;
        this._toolbarEventNs = null;

        _adapterRegistry.delete(tableNode);

        if (this._headerTickGuard) {
            const $c = $(this._instance.table().container());
            if ($c.length) $c[0].removeEventListener('click', this._headerTickGuard, true);
            this._headerTickGuard = null;
        }

        this._instance.destroy();
    }

    _showColVis() {
        if (!window.GenericModal) {
            console.error('DataTableAdapter: GenericModal is required for colvis.');
            return;
        }

        const self = this;
        const columns = this._config.columns;
        
        const builder = window.GenericModal.create({
            title: 'Manage Column Visibility',
            size: 'md'
        });

        // Build items using the trusted html tag
        const items = columns.map((col, idx) => {
            // Skip columns marked as no-toggle (e.g. checkboxes, actions)
            if (col.noToggle === true) return '';
            // Skip columns with dt-hidden class (internal metadata)
            if (col.className && col.className.includes('dt-hidden')) return '';
            // Skip columns without titles (unless data property is present)
            if (!col.title && !col.data) return '';

            const isVisible = this._instance.column(idx).visible();
            const id = `col-vis-${idx}`;
            const title = col.title || col.data || `Column ${idx + 1}`;
            
            // Strip HTML from title if it's a string
            const cleanTitle = typeof title === 'string' ? title.replace(/<[^>]*>?/gm, '') : title;

            return window.GenericModal.html`
                <div class="dt-colvis-item">
                    <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" id="${id}" ${isVisible ? 'checked' : ''} data-col-idx="${idx}">
                        <label class="form-check-label" for="${id}">${cleanTitle}</label>
                    </div>
                </div>
            `;
        });

        const bodyContent = window.GenericModal.html`
            <div class="dt-colvis-container">
                <div class="dt-colvis-grid">
                    ${items}
                </div>
            </div>
        `;

        builder.addHtml(bodyContent);

        // Add Bulk Actions in Footer
        builder.withButtons(b => [
            b.add('Show All')
                .withClass('btn-sm btn-primary')
                .onClick((_data, _body, $modalEl) => {
                    $modalEl.find('.form-check-input').prop('checked', true).trigger('change');
                    return false;
                }),
            b.add('Hide All')
                .withClass('btn-sm btn-secondary')
                .onClick((_data, _body, $modalEl) => {
                    $modalEl.find('.form-check-input').prop('checked', false).trigger('change');
                    return false;
                })
        ]);

        const modal = builder.show();
        const $modal = modal.elements?.$modal || $(modal.id ? `#${modal.id}` : '.modal.show');

        $modal.on('change', '.form-check-input', function() {
            const idx = $(this).data('col-idx');
            const checked = $(this).is(':checked');
            self._instance.column(idx).visible(checked);
            self.recalculate(); // Fix header alignment
        });
    }

    /**
     * Static helper to retrieve row data for a specific element within a table.
     * Useful for external integration (e.g. GenericModal action delegation).
     */
    static getRowData(selector, element) {
        const $table = $(selector);
        const $tr = $(element).closest('tr');
        if ($table.length && $.fn.DataTable.isDataTable($table)) {
            return $table.DataTable().row($tr).data();
        }
        return null;
    }
}
